import {CopyToClipboardButton} from '@github-ui/copy-to-clipboard/Button'
import {CreateIssueButton} from '@github-ui/issue-create/CreateIssueButton'
import {noop} from '@github-ui/noop'
import {useContainerBreakpoint} from '@github-ui/use-container-breakpoint'
import {CopyIcon, KebabHorizontalIcon, XIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, Button, IconButton} from '@primer/react'
import {Box} from '@primer/styled-react'
import {useMemo, useRef} from 'react'
import {graphql, useFragment} from 'react-relay'

import {BUTTON_LABELS} from '../../constants/buttons'
import {LABELS} from '../../constants/labels'
import {TEST_IDS} from '../../constants/test-ids'
import type {OptionConfig} from '../OptionConfig'
import type {HeaderMenu$key} from './__generated__/HeaderMenu.graphql'
import styles from './HeaderMenu.module.css'

export type HeaderMenuBaseProps = {
  optionConfig: OptionConfig
  setIsIssueTitleEditActive?: (isActive: boolean) => void
  containerRef?: React.RefObject<HTMLDivElement>
}

type HeaderMenuProps = {
  headerMenuData: HeaderMenu$key
} & HeaderMenuBaseProps

const HeaderMenuFragment = graphql`
  fragment HeaderMenu on Issue {
    viewerCanUpdateNext
    repository {
      name
      owner {
        login
      }
      isArchived
    }
    url
  }
`

export function HeaderMenu({
  headerMenuData,
  setIsIssueTitleEditActive,
  containerRef,
  optionConfig: {
    customEditMenuEntries,
    navigate,
    showIssueCreateButton,
    additionalHeaderActions,
    onClose,
    singleKeyShortcutsEnabled,
    emojiSkinTonePreference,
    useViewportQueries,
  },
}: HeaderMenuProps) {
  const {viewerCanUpdateNext, url, repository} = useFragment(HeaderMenuFragment, headerMenuData)

  const breakpoint = useContainerBreakpoint(containerRef?.current ?? null)

  const anchorRef = useRef<HTMLButtonElement>(null)

  const editIssueTitleButton = useMemo(
    () =>
      viewerCanUpdateNext ? (
        <Button
          data-testid={TEST_IDS.editIssueTitleButton}
          aria-label={LABELS.editTitle}
          onClick={() => setIsIssueTitleEditActive && setIsIssueTitleEditActive(true)}
          size="medium"
        >
          {BUTTON_LABELS.editTitle}
        </Button>
      ) : null,
    [setIsIssueTitleEditActive, viewerCanUpdateNext],
  )

  return (
    <div className={styles.menuActionsContainer}>
      {editIssueTitleButton}
      <Box
        sx={{
          flexGrow: useViewportQueries ? ['1', '1', '0', '0'] : breakpoint(['1', '1', '0', '0']),
        }}
        className={styles.buttonContainer}
      >
        {showIssueCreateButton && !repository.isArchived && (
          <CreateIssueButton
            label={BUTTON_LABELS.new}
            navigate={navigate}
            optionConfig={{
              showRepositoryPicker: false,
              issueCreateArguments: {
                repository: {
                  owner: repository.owner.login,
                  name: repository.name,
                },
              },
              showFullScreenButton: true,
              // Only navigate if we're in the Repo#Index (ie, have a scoped repository)
              navigateToFullScreenOnTemplateChoice: navigate !== noop && repository !== null,
              singleKeyShortcutsEnabled,
              emojiSkinTonePreference,
              canBypassTemplateSelection: true,
              navigate,
            }}
          />
        )}
      </Box>
      <CopyToClipboardButton
        textToCopy={url}
        ariaLabel={BUTTON_LABELS.copyIssueLink}
        icon={CopyIcon}
        tooltipProps={{direction: 's'}}
      />
      {additionalHeaderActions}
      {customEditMenuEntries && (
        <ActionMenu anchorRef={anchorRef}>
          <ActionMenu.Anchor>
            <IconButton
              variant="invisible"
              icon={KebabHorizontalIcon}
              aria-label={BUTTON_LABELS.issueActions}
              ref={anchorRef}
            />
          </ActionMenu.Anchor>
          <ActionMenu.Overlay>
            <ActionList>{customEditMenuEntries.map(e => e)}</ActionList>
          </ActionMenu.Overlay>
        </ActionMenu>
      )}
      {onClose && (
        <IconButton variant="invisible" aria-label={BUTTON_LABELS.closePanel} icon={XIcon} onClick={onClose} />
      )}
    </div>
  )
}

export function StickyHeaderMenu({
  headerMenuData,
  optionConfig: {customEditMenuEntries, additionalHeaderActions, onClose},
}: HeaderMenuProps) {
  const {url} = useFragment(HeaderMenuFragment, headerMenuData)

  const anchorRef = useRef<HTMLButtonElement>(null)

  return (
    <div className={styles.menuActionsContainer}>
      <CopyToClipboardButton
        textToCopy={url}
        ariaLabel={BUTTON_LABELS.copyIssueLink}
        icon={CopyIcon}
        tooltipProps={{direction: 's'}}
      />
      {additionalHeaderActions}
      {customEditMenuEntries && (
        <ActionMenu anchorRef={anchorRef}>
          <ActionMenu.Anchor>
            <IconButton
              variant="invisible"
              icon={KebabHorizontalIcon}
              aria-label={BUTTON_LABELS.issueActions}
              tooltipDirection="n"
              ref={anchorRef}
              className={styles.kebabMenuButton}
            />
          </ActionMenu.Anchor>
          <ActionMenu.Overlay>
            <ActionList>{customEditMenuEntries.map(e => e)}</ActionList>
          </ActionMenu.Overlay>
        </ActionMenu>
      )}
      {onClose && (
        <IconButton variant="invisible" aria-label={BUTTON_LABELS.closePanel} icon={XIcon} onClick={onClose} />
      )}
    </div>
  )
}

try{ HeaderMenu.displayName ||= 'HeaderMenu' } catch {}
try{ StickyHeaderMenu.displayName ||= 'StickyHeaderMenu' } catch {}